<template>
  <ErrorBoundary 
    :error-title="$t('signUp.errorTitle')" 
    :error-message="$t('signUp.errorMessage')"
  >
    <div class="min-h-screen bg-slate-50 flex flex-col justify-center py-12 px-2">
      <BlogMeta 
        :title="$t('signUp.meta.title')"
        :description="$t('signUp.meta.description')"
        :noIndex="true"
      />

      <!-- Contenedor principal del registro -->
      <div class="max-w-lg mx-auto bg-white rounded-lg shadow-sm p-1 sm:p-5 overflow-hidden">
        <div class="py-8 px-4 sm:px-10">

          <!-- Panel de éxito: verificación de email pendiente -->
          <div v-if="registrationSuccess" class="text-center">
            <div class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
              <svg class="h-10 w-10 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-3">¡Revisa tu correo!</h2>
            <p class="text-gray-600 mb-2">
              Te hemos enviado un enlace de verificación a <strong>{{ email }}</strong>.
            </p>
            <p class="text-gray-500 text-sm mb-6">
              Haz clic en el enlace del email para activar tu cuenta. Recuerda revisar la carpeta de spam si no lo ves.
            </p>

            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <div class="flex">
                <svg class="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <div class="ml-3 text-sm text-blue-700">
                  <p class="font-medium text-blue-800 mb-1">Consejos</p>
                  <ul class="list-disc pl-4 space-y-1">
                    <li>Revisa tu carpeta de spam o promociones</li>
                    <li>El enlace expira en 24 horas</li>
                    <li>Puedes usar la app mientras tanto</li>
                  </ul>
                </div>
              </div>
            </div>

            <p class="text-sm text-gray-400 mb-4">
              Buscando colivings en <span class="font-semibold text-indigo-600">{{ redirectCountdown }}</span> segundos...
            </p>

            <button
              @click="goToUserDashboard"
              class="w-full inline-flex items-center justify-center py-2 px-4 border border-indigo-200 rounded-full shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50 transition-colors duration-200"
            >
              Buscar colivings ahora
            </button>
          </div>

          <!-- Título del registro (oculto tras el registro exitoso) -->
          <div v-if="!registrationSuccess" class="mb-6">
            <h1 class="text-2xl font-bold text-gray-900 text-center">
              {{ hasPendingApplication ? '¡Completa tu registro!' : $t('signUp.title') }}
            </h1>
            
            <!-- Mensaje especial para usuarios con solicitud temporal -->
            <div v-if="hasPendingApplication && pendingApplicationData" class="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                  <svg class="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div class="flex-1">
                  <h3 class="text-sm font-semibold text-blue-800 mb-1">
                    🏠 ¡Tienes una solicitud de coliving guardada!
                  </h3>
                  <p class="text-blue-700 text-sm mb-2">
                    Hemos encontrado tu solicitud para: <strong>{{ pendingApplicationData.colivingTitle || 'el coliving seleccionado' }}</strong>
                  </p>
                  <p class="text-blue-600 text-xs">
                    Completa tu registro y tu solicitud será procesada automáticamente.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!registrationSuccess">
          <form class="space-y-6" @submit.prevent="signUp">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2 text-center">{{ $t('signUp.userType.label') }}</label>
              <div class="mt-2 flex justify-center">
                <div class="inline-flex rounded-lg p-1 bg-gray-50 shadow-sm space-x-2">
                  <button 
                    type="button"
                    @click="handleUserTypeChange('agent')"
                    :class="[
                      'px-6 py-2 text-sm font-medium rounded-full transition-all duration-200',
                      userType === 'agent'
                        ? 'bg-white text-indigo-600 border border-indigo-200 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50 border border-transparent'
                    ]"
                  >
                    Agente
                  </button>
                  <!-- Owner button - OCULTO -->
                  <button 
                    v-if="false"
                    type="button"
                    disabled
                    :class="[
                      'px-6 py-2 text-sm font-medium rounded-full transition-all duration-200',
                      'text-gray-400 bg-gray-100 cursor-not-allowed opacity-50'
                    ]"
                    title="Opción temporalmente desactivada"
                  >
                    {{ $t('signUp.userType.owner') }}
                  </button>
                  <button 
                    type="button"
                    @click="handleUserTypeChange('applicant')"
                    :class="[
                      'px-6 py-2 text-sm font-medium rounded-full transition-all duration-200',
                      userType === 'applicant'
                        ? 'bg-white text-indigo-600 border border-indigo-200 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50 border border-transparent'
                    ]"
                  >
                    {{ $t('signUp.userType.applicant') }}
                  </button>
                 
                </div>
              </div>
              <input type="hidden" :value="userType" name="userType" required>
                
                <!-- Información para Agentes — switch + card estilo FeatureLanding -->
                <div v-if="userType === 'agent'" class="mt-4">
                  <h4 class="text-sm font-semibold text-gray-900 mb-4 text-center">{{ $t('signUp.agentPlan.selectPlan') }}</h4>

                  <!-- Toggle switch -->
                  <div class="flex justify-center mb-4">
                    <div class="inline-flex rounded-lg p-1 bg-gray-100 space-x-1">
                      <button
                        type="button"
                        @click="selectedAgentRole = 'basic'"
                        :class="[
                          'px-5 py-2 text-sm font-medium rounded-md transition-all duration-200',
                          selectedAgentRole === 'basic'
                            ? 'bg-white text-indigo-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-800'
                        ]"
                      >
                        {{ $t('signUp.agentPlan.basic.title') }}
                      </button>
                      <button
                        type="button"
                        @click="selectedAgentRole = 'master'"
                        :class="[
                          'px-5 py-2 text-sm font-medium rounded-md transition-all duration-200',
                          (selectedAgentRole === 'master' || selectedAgentRole === 'commission')
                            ? 'bg-white text-purple-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-800'
                        ]"
                      >
                        {{ $t('signUp.agentPlan.pro.title') }}
                        <span class="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600">★</span>
                      </button>
                    </div>
                  </div>

                  <!-- Card dinámica -->
                  <div
                    :class="selectedAgentRole === 'basic'
                      ? 'border-indigo-300 bg-gradient-to-b from-indigo-50/60 to-white'
                      : 'border-purple-300 bg-gradient-to-b from-purple-50/60 to-white'"
                    class="rounded-xl border-2 p-5 transition-all duration-300"
                  >
                    <!-- Badge + Precio en fila -->
                    <div class="flex items-start justify-between mb-1">
                      <div>
                        <span
                          :class="selectedAgentRole === 'basic' ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'"
                          class="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold mb-2"
                        >
                          {{ selectedAgentRole === 'basic' ? $t('signUp.agentPlan.basic.freeTrial') : $t('signUp.agentPlan.pro.freeTrial') }}
                        </span>
                        <div>
                          <span
                            :class="selectedAgentRole === 'basic' ? 'text-indigo-600' : 'text-purple-600'"
                            class="text-3xl font-bold"
                          >
                            {{ selectedAgentRole === 'basic' ? $t('signUp.agentPlan.basic.price') : $t('signUp.agentPlan.pro.price') }}
                          </span>
                          <span class="text-sm text-gray-400 ml-1">
                            {{ selectedAgentRole === 'basic' ? $t('signUp.agentPlan.basic.perMonth') : $t('signUp.agentPlan.pro.perMonth') }}
                          </span>
                        </div>
                      </div>
                      <!-- Badge "Más popular" para Pro -->
                      <span
                        v-if="selectedAgentRole !== 'basic'"
                        class="px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-600 text-white shadow-sm flex-shrink-0"
                      >
                        {{ $t('partnerServices.services.featured.badge') }}
                      </span>
                    </div>

                    <!-- Título -->
                    <p class="text-base font-bold text-gray-900 mb-4">
                      {{ selectedAgentRole === 'basic' ? $t('signUp.agentPlan.basic.title') : $t('signUp.agentPlan.pro.title') }}
                    </p>
                    <div class="h-px bg-gray-200 mb-4"></div>

                    <!-- Features Basic -->
                    <div v-if="selectedAgentRole === 'basic'" class="space-y-2.5">
                      <div v-for="f in basicFeatures" :key="f.key" class="flex items-start gap-2.5">
                        <div v-if="f.bold" class="w-5 h-5 mt-0.5 flex-shrink-0 bg-indigo-600 rounded-full flex items-center justify-center">
                          <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                        </div>
                        <svg v-else class="w-5 h-5 mt-0.5 flex-shrink-0 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                        <span :class="f.bold ? 'text-sm font-semibold text-gray-900' : 'text-sm text-gray-500'">{{ $t(f.key) }}</span>
                      </div>
                    </div>

                    <!-- Features Pro -->
                    <div v-else class="space-y-2.5">
                      <div v-for="f in proFeatures" :key="f.key" class="flex items-start gap-2.5">
                        <div class="w-5 h-5 mt-0.5 flex-shrink-0 bg-purple-600 rounded-full flex items-center justify-center">
                          <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                        </div>
                        <span class="text-sm font-semibold text-gray-900">{{ $t(f.key) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Bloques de ayuda para Agentes -->
                <div v-if="userType === 'agent'" class="mt-5 space-y-2.5">
                  <!-- Bloque 1: ¿Tienes dudas? -->
                  <button
                    type="button"
                    @click="showUpgradeModal = true"
                    class="group w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border border-gray-100 bg-white hover:border-indigo-200 hover:bg-indigo-50/30 transition-all duration-200 text-left"
                  >
                    <div class="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <svg class="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-gray-900">{{ $t('signUp.agentHelp.doubts.title') }}</p>
                      <p class="text-xs text-indigo-500 font-medium mt-0.5">{{ $t('signUp.agentHelp.doubts.cta') }}</p>
                    </div>
                    <svg class="flex-shrink-0 w-4 h-4 text-gray-300 group-hover:text-indigo-400 group-hover:translate-x-0.5 transition-all duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <!-- Bloque 2: ¿Buscas servicios para tu empresa? -->
                  <button
                    type="button"
                    @click="showPartnerModal = true"
                    class="group w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/60 transition-all duration-200 text-left"
                  >
                    <div class="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center">
                      <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-gray-900">{{ $t('signUp.agentHelp.services.title') }}</p>
                      <p class="text-xs text-gray-400 font-medium mt-0.5">{{ $t('signUp.agentHelp.services.cta') }}</p>
                    </div>
                    <svg class="flex-shrink-0 w-4 h-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <!-- Información para Owner (oculta) -->
                <div v-if="userType === 'owner'" class="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p class="text-sm text-gray-500 italic">Opción temporalmente desactivada</p>
                </div>
                
                <!-- Información para Applicants -->
                <div v-if="userType === 'applicant'" class="mt-4 p-3">
                  <p class="text-sm text-gray-700">
                    Guarda favoritos, solicita información y encuentra tu coliving ideal.
                  </p>
                </div>
            </div>

            <!-- Google Sign-In para Applicants -->
            <div v-if="userType === 'applicant'" class="mb-6">
              <button
                type="button"
                @click="signUpWithGoogle"
                :disabled="isLoading"
                class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <svg class="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>{{ $t('signUp.continueWithGoogle') }}</span>
              </button>
              
              <!-- Separador -->
              <div class="relative my-6">
                <div class="absolute inset-0 flex items-center">
                  <div class="w-full border-t border-gray-300"></div>
                </div>
                <div class="relative flex justify-center text-sm">
                  <span class="px-2 bg-white text-gray-500">{{ $t('signUp.orContinueWith') }}</span>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <label for="fullName" class="block text-sm font-medium text-gray-700 mb-2">{{ $t('signUp.fullName') }}</label>
              <input v-model="fullName" type="text" id="fullName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900">
            </div>

            <div class="mb-4">
              <label for="email" class="block text-sm font-medium text-gray-700 mb-2">{{ $t('signUp.email') }}</label>
              <input v-model="email" type="email" id="email" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900">
            </div>

            <!-- Campos adicionales para Agentes -->
            <div v-if="userType === 'agent'" class="space-y-4 mb-4">              
              <div>
                <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">
                  {{ $t('signUp.agentInfo.phone') }}
                  <span class="text-red-500">*</span>
                </label>
                <input 
                  v-model="phone" 
                  type="tel" 
                  id="phone" 
                  required
                  placeholder="Ej: +34 600 000 000"
                  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                >
              </div>

              
            </div>

            <div class="mb-4">
              <label for="password" class="block text-sm font-medium text-gray-700 mb-2">{{ $t('signUp.password.label') }}</label>
              <div class="relative">
                <input 
                  v-model="password" 
                  :type="showPassword ? 'text' : 'password'" 
                  id="password" 
                  required 
                  minlength="8"
                  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                >
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <!-- Ojo abierto (mostrar contraseña) -->
                  <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <!-- Ojo cerrado (ocultar contraseña) -->
                  <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                </button>
              </div>
              <p class="mt-1 text-sm text-gray-500">{{ $t('signUp.password.requirements') }}</p>
            </div>

            <div class="mb-4">
              <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-2">{{ $t('signUp.confirmPassword') }}</label>
              <div class="relative">
                <input 
                  v-model="confirmPassword" 
                  :type="showConfirmPassword ? 'text' : 'password'" 
                  id="confirmPassword" 
                  required 
                  minlength="8"
                  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                >
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <!-- Ojo abierto (mostrar contraseña) -->
                  <svg v-if="showConfirmPassword" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <!-- Ojo cerrado (ocultar contraseña) -->
                  <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="mb-4">
              <div class="flex items-center">
                <input 
                  v-model="agreeTerms" 
                  id="agreeTerms" 
                  type="checkbox" 
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" 
                  required
                >
                <label for="agreeTerms" class="ml-2 block text-sm text-gray-900">
                  {{ $t('signUp.terms.agree') }}
                  <button 
                    type="button"
                    @click="showTermsModal = true"
                    class="font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    {{ $t('signUp.terms.link') }}
                  </button>
                </label>
              </div>
            </div>

            <div class="mb-6">
              <div class="flex items-center">
                <input 
                  v-model="confirmAge" 
                  id="confirmAge" 
                  type="checkbox" 
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" 
                  required
                >
                <label for="confirmAge" class="ml-2 block text-sm text-gray-900">
                  {{ $t('signUp.age.confirm') }}
                </label>
              </div>
            </div>

            <!-- Terms Modal with Teleport -->
            <Teleport to="body">
              <div v-if="showTermsModal" 
                   class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                   @click.self="closeTermsModal">
                <TermsModal
                  v-model:show="showTermsModal"
                  @close="closeTermsModal"
                  @accept="handleTermsAccept"
                />
              </div>
            </Teleport>

            <div class="mb-6">
              <button type="submit" class="w-full inline-flex items-center justify-center py-2 px-4 border border-indigo-200 rounded-full shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" :disabled="isLoading">
                <LoadingSpinner v-if="isLoading" size="small" color="indigo" class="mr-2" />
                <span v-if="isLoading">{{ $t('signUp.signingUpButton', 'Registrando...') }}</span>
                <span v-else>{{ $t('signUp.signupButton') }}</span>
              </button>
            </div>
          </form>

          <p class="mt-6 text-center text-sm text-gray-600">
            {{ $t('signUp.haveAccount') }}
            <router-link to="/login" class="font-medium text-indigo-600 hover:text-indigo-500">
              {{ $t('signUp.loginLink') }}
            </router-link>
          </p>
          </div><!-- fin v-if="!registrationSuccess" -->
        </div>
      </div>
    </div>
    <ToastComponent v-if="toastMessage" :message="toastMessage" />
    <UpgradeToProModal :is-visible="showUpgradeModal" @close="showUpgradeModal = false" />
    <PartnerContactModal :is-visible="showPartnerModal" @close="showPartnerModal = false" />
  </ErrorBoundary>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAnalytics } from '@/composables/useAnalytics'
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  updateProfile,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signOut
} from 'firebase/auth'
import { 
  doc, 
  setDoc,
  getDoc
} from 'firebase/firestore'
import { db } from '../firebase'
import TermsModal from './TermsModal.vue'
import ToastComponent from './ToastComponent.vue'
import ErrorBoundary from './ErrorBoundary.vue'
import BlogMeta from './BlogMeta.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import UpgradeToProModal from './agent/UpgradeToProModal.vue'
import PartnerContactModal from './PartnerContactModal.vue'
import { verifyRecaptchaWithBackend, getRecaptchaTokenForApi, getRecaptchaErrorMessage } from '@/utils/recaptcha'
// managedOwner check ahora se hace via backend endpoint POST /api/users/check-and-link-managed-owner

// Function to generate a secure token for email verification
const generateSecureToken = () => {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
}

export default {
  name: 'SignUp',
  components: {
    TermsModal,
    ToastComponent,
    ErrorBoundary,
    BlogMeta,
    LoadingSpinner,
    UpgradeToProModal,
    PartnerContactModal
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const route = useRoute()
    const auth = getAuth()
    const { trackSignUp, trackSignUpUserTypeChange, trackSignUpError } = useAnalytics()
    const userType = ref('applicant')
    const previousUserType = ref('applicant') // Para tracking de cambios
    const selectedAgentRole = ref('basic') // Valor por defecto: Plan Basic
    const fullName = ref('')
    const email = ref('')
    const password = ref('')
    const confirmPassword = ref('')
    const showPassword = ref(false)
    const showConfirmPassword = ref(false)
    const agreeTerms = ref(false)
    const confirmAge = ref(false)
    const showTermsModal = ref(false)
    const toastMessage = ref('')
    const showUpgradeModal = ref(false)
    const showPartnerModal = ref(false)
    const isLoading = ref(false)

    // Estado tras registro exitoso de applicant
    const registrationSuccess = ref(false)
    const redirectCountdown = ref(10)
    let countdownInterval = null
    
    // Campos adicionales para agentes
    const phone = ref('')
    const dni = ref('')
    const agencyName = ref('')

    // Variables para usuarios que vienen de solicitud temporal
    const registrationToken = ref(null)
    const hasPendingApplication = ref(false)
    const pendingApplicationData = ref(null)

    // Función para obtener la ruta de redirección según el tipo de usuario
    const getRedirectByUserType = (userType) => {
      switch (userType) {
        case 'admin':
          return '/admin'
        case 'agent':
          return '/agent-dashboard'
        case 'applicant':
          return '/contracts'
        case 'owner':
        case 'managedOwner':
        default:
          return '/contracts'
      }
    }

    const blockDisabledAccount = async (userData) => {
      if (!userData || !['suspended', 'deleted'].includes(userData.accountStatus)) {
        return false
      }

      await signOut(auth)
      showToast(
        userData.accountStatus === 'deleted'
          ? 'Esta cuenta ha sido eliminada.'
          : 'Esta cuenta está suspendida. Contacta con soporte.'
      )
      return true
    }

    // Función para redirigir usuarios ya autenticados
    const redirectAuthenticatedUser = async () => {
      try {
        const user = auth.currentUser
        if (!user) return

        // Obtener el tipo de usuario desde Firestore
        const userDocRef = doc(db, 'users', user.uid)
        const userDoc = await getDoc(userDocRef)
        
        if (userDoc.exists()) {
          const userData = userDoc.data()

          if (await blockDisabledAccount(userData)) {
            return
          }
          
          // Verificar si el email está verificado
          if (!userData.emailVerified) {
            console.log('[SignUp] Usuario no verificado, permitiendo acceso a /signup')
            return
          }
          
          const userType = userData.userType
          const redirectPath = getRedirectByUserType(userType)
          
          console.log(`[SignUp] Usuario ya autenticado (${userType}), redirigiendo a ${redirectPath}`)
          router.push(redirectPath)
        }
      } catch (error) {
        console.error('[SignUp] Error al verificar usuario autenticado:', error)
      }
    }

    let authUnsubscribe = null

    // Leer el parámetro userType de la URL y establecer el valor por defecto
    onMounted(async () => {
      // PRIMERO: Verificar si el usuario ya está autenticado y redirigirlo
      const currentUser = auth.currentUser
      if (currentUser) {
        await redirectAuthenticatedUser()
        return
      }
      
      authUnsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          await redirectAuthenticatedUser()
        }
      })
      
      const urlUserType = route.query.userType
      // Solo permitir 'applicant' o 'agent' (owner está desactivado)
      if (urlUserType === 'applicant' || urlUserType === 'agent') {
        userType.value = urlUserType
      } else if (urlUserType === 'owner') {
        // Redirigir propietarios a applicant por ahora
        userType.value = 'applicant'
      }

      // Pre-seleccionar plan de agente si viene en query params
      const urlPlan = route.query.plan
      if (urlPlan === 'master' || urlPlan === 'pro') {
        selectedAgentRole.value = 'master'
      } else if (urlPlan === 'basic') {
        selectedAgentRole.value = 'basic'
      } else if (urlPlan === 'commission') {
        selectedAgentRole.value = 'commission'
      }

      // Pre-rellenar email y nombre si vienen en query params (para invitaciones de agente)
      const urlEmail = route.query.email
      const urlName = route.query.name
      const invitationToken = route.query.invitationToken
      
      if (urlEmail && typeof urlEmail === 'string') {
        email.value = urlEmail
        console.log('[SignUp] Email pre-rellenado desde invitación:', urlEmail)
      }
      
      if (urlName && typeof urlName === 'string') {
        fullName.value = urlName
        console.log('[SignUp] Nombre pre-rellenado desde invitación:', urlName)
      }
      
      if (invitationToken && typeof invitationToken === 'string') {
        // Guardar token de invitación para procesarlo después del registro
        sessionStorage.setItem('agentInvitationToken', invitationToken)
        console.log('[SignUp] Token de invitación guardado para procesamiento posterior')
      }

      // Detectar token de registro para usuarios de solicitud temporal
      const token = route.query.registrationToken
      if (token) {
        registrationToken.value = token
        
        try {
          // Verificar si existe una solicitud temporal vinculada
          const response = await fetch(`/api/colivings/temp-application/${token}`)
          if (response.ok) {
            const tempApplication = await response.json()
            hasPendingApplication.value = true
            pendingApplicationData.value = tempApplication
            
            // Pre-rellenar email si viene de solicitud temporal
            if (tempApplication.applicantEmail) {
              email.value = tempApplication.applicantEmail
            }
            if (tempApplication.applicantName) {
              fullName.value = tempApplication.applicantName
            }
            
            // Forzar userType a 'applicant' para solicitudes de coliving
            userType.value = 'applicant'
          }
        } catch (error) {
          console.error('Error verificando solicitud temporal:', error)
        }
      }
      
      // Forzar el scroll al inicio de la página
      window.scrollTo(0, 0)
    })

    onUnmounted(() => {
      if (authUnsubscribe) {
        authUnsubscribe()
        authUnsubscribe = null
      }
      if (countdownInterval) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }
    })

    const basicFeatures = [
      { key: 'featureLanding.agentBasic.features.marketplaceHighlight', bold: true },
      { key: 'featureLanding.agentBasic.features.verification', bold: true },
      { key: 'featureLanding.agentBasic.features.aiSelection', bold: true },
      { key: 'featureLanding.agentBasic.features.digitalContracts', bold: false },
      { key: 'featureLanding.agentBasic.features.automation', bold: true },
      { key: 'featureLanding.agentBasic.features.incidentSystem', bold: false },
      { key: 'featureLanding.agentBasic.features.dashboard', bold: false },
    ]

    const proFeatures = [
      { key: 'featureLanding.agentPro.features.allBasic' },
      { key: 'featureLanding.agentPro.features.marketplaceFeatured' },
      { key: 'featureLanding.agentPro.features.clientManagement' },
      { key: 'featureLanding.agentPro.features.unlimitedColivings' },
      { key: 'featureLanding.agentPro.features.whiteLabel' },
      { key: 'featureLanding.agentPro.features.unlimitedTeam' },
      { key: 'featureLanding.agentPro.features.support247' },
    ]

    const showToast = (message) => {
      toastMessage.value = message
      setTimeout(() => {
        toastMessage.value = ''
      }, 3000)
    }

    // Función para manejar cambio de tipo de usuario con tracking
    const handleUserTypeChange = (newType) => {
      if (newType !== userType.value) {
        trackSignUpUserTypeChange({
          newType,
          previousType: previousUserType.value
        })
        previousUserType.value = userType.value
        userType.value = newType
      }
    }

    const goToUserDashboard = () => {
      if (countdownInterval) clearInterval(countdownInterval)
      router.push('/encuentratucoliving')
    }

    const startRedirectCountdown = () => {
      redirectCountdown.value = 10
      countdownInterval = setInterval(() => {
        redirectCountdown.value--
        if (redirectCountdown.value <= 0) {
          clearInterval(countdownInterval)
          router.push('/encuentratucoliving')
        }
      }, 1000)
    }

    const signUpWithGoogle = async () => {
      if (isLoading.value) return
      
      isLoading.value = true

      try {
        try {
          await verifyRecaptchaWithBackend('google_signup')
        } catch (recaptchaErr) {
          console.error('[SignUp Google] reCAPTCHA failed:', recaptchaErr)
          showToast(getRecaptchaErrorMessage(recaptchaErr))
          return
        }

        const auth = getAuth()
        const provider = new GoogleAuthProvider()
        provider.setCustomParameters({
          prompt: 'select_account'
        })

        // Abrir popup de Google
        const result = await signInWithPopup(auth, provider)
        const user = result.user

        console.log('[SignUp Google] Usuario autenticado:', user.uid)

        // Verificar si el usuario ya existe en Firestore
        const userDocRef = doc(db, 'users', user.uid)
        const userDoc = await getDoc(userDocRef)

        if (userDoc.exists()) {
          const existingData = userDoc.data()
          if (await blockDisabledAccount(existingData)) {
            return
          }
          // El usuario ya existe - redirigir a login
          showToast('Ya tienes una cuenta. Redirigiendo a tus estancias...')
          await new Promise(resolve => setTimeout(resolve, 100))
          await router.push('/contracts')
          return
        }

        // Usuario nuevo - crear documento en Firestore
        trackSignUp({ metodo: 'google', tipoUsuario: 'applicant' })
        const userData = {
          email: user.email,
          fullName: user.displayName || '',
          userType: 'applicant',
          profileComplete: false,
          createdAt: new Date(),
          authProvider: 'google',
          emailVerified: true,
          emailVerifiedAt: new Date()
        }

        console.log('[SignUp Google] Creando documento en Firestore para applicant')
        await setDoc(userDocRef, userData)

        // Completar registro Google en el backend (envía welcome email server-side, como el flujo tradicional)
        try {
          const recaptchaToken = await getRecaptchaTokenForApi('google_signup')
          const registrationResponse = await fetch('/api/email/complete-google-registration', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: user.uid,
              userEmail: user.email,
              userName: user.displayName || user.email?.split('@')[0] || 'Usuario',
              ...(recaptchaToken && { recaptchaToken }),
            })
          })
          const registrationResult = await registrationResponse.json()
          if (!registrationResult.success) {
            console.warn('[SignUp Google] Registro backend no completado:', registrationResult.message)
          }
        } catch (registrationError) {
          console.error('[SignUp Google] Error completando registro en backend (no crítico):', registrationError)
        }

        // Verificar si hay una solicitud temporal pendiente
        const tempApplicationId = sessionStorage.getItem('tempApplicationId')
        if (tempApplicationId) {
          console.log('[SignUp Google] Vinculando solicitud temporal:', tempApplicationId)
          try {
            const tempAppRef = doc(db, 'temporaryApplications', tempApplicationId)
            const tempAppDoc = await getDoc(tempAppRef)
            
            if (tempAppDoc.exists()) {
              const tempData = tempAppDoc.data()
              
              // Crear aplicación real
              const applicationsRef = doc(db, 'colivingApplications', user.uid + '_' + tempData.colivingId)
              await setDoc(applicationsRef, {
                applicantId: user.uid,
                colivingId: tempData.colivingId,
                status: 'submitted',
                createdAt: new Date(),
                message: tempData.message || '',
                temporaryApplicationId: tempApplicationId
              })
              
              console.log('[SignUp Google] Solicitud vinculada correctamente')
              sessionStorage.removeItem('tempApplicationId')
              showToast('¡Registro completado! Tu solicitud ha sido enviada.')
            }
          } catch (error) {
            console.error('[SignUp Google] Error vinculando solicitud temporal:', error)
          }
        } else {
          showToast(t('signUp.success'))
        }

        // Redirigir a búsqueda de colivings
        await new Promise(resolve => setTimeout(resolve, 100))
        await router.push('/encuentratucoliving')

      } catch (error) {
        console.error('[SignUp Google] Error:', error)
        
        // Track signup error
        trackSignUpError({
          method: 'google',
          errorCode: error.code || 'unknown',
          errorMessage: error.message || '',
          userType: 'applicant'
        })

        if (error.code === 'auth/popup-closed-by-user') {
          showToast('Has cerrado la ventana de Google')
        } else if (error.code === 'auth/cancelled-popup-request') {
          showToast('Solicitud cancelada')
        } else if (error.code === 'auth/popup-blocked') {
          showToast('El popup fue bloqueado. Por favor, permite popups en tu navegador.')
        } else {
          showToast('Error al registrarse con Google. Intenta de nuevo.')
        }
      } finally {
        isLoading.value = false
      }
    }

    const signUp = async () => {
      // Validar que la contraseña cumpla con los requisitos
      const hasMinLength = password.value.length >= 8;
      const hasNumber = /\d/.test(password.value);
      const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password.value);
      
      if (!hasMinLength || !hasNumber || !hasSymbol) {
        showToast(t('signUp.password.validationError'));
        return;
      }

      if (password.value !== confirmPassword.value) {
        showToast(t('signUp.password.mismatch'));
        return;
      }

      if (!agreeTerms.value) {
        showToast(t('signUp.terms.required'));
        return;
      }

      if (!confirmAge.value) {
        showToast(t('signUp.age.required'));
        return;
      }

      // Validar campos adicionales para agentes
      if (userType.value === 'agent') {
        if (!phone.value) {
          showToast(t('signUp.agentInfo.phoneRequired'));
          return;
        }
      }

      isLoading.value = true

      try {
        try {
          await verifyRecaptchaWithBackend('signup')
        } catch (recaptchaErr) {
          console.error('[SignUp] reCAPTCHA failed:', recaptchaErr)
          showToast(getRecaptchaErrorMessage(recaptchaErr))
          return
        }

        // ✅ Verificar si viene de una invitación de agente
        const agentInvitationToken = sessionStorage.getItem('agentInvitationToken')
        const isAgentInvitation = agentInvitationToken && userType.value === 'agent'

        // ── PASO 1: Crear usuario en Firebase Auth ──
        const auth = getAuth()
        const userCredential = await createUserWithEmailAndPassword(auth, email.value, password.value)
        const user = userCredential.user
        
        console.log('[SignUp] Usuario creado en Firebase Auth:', user.uid)
        trackSignUp({ metodo: 'email', tipoUsuario: userType.value })

        // Update user profile
        await updateProfile(user, {
          displayName: fullName.value
        })

        // ✅ LÓGICA PARA INVITACIONES DE AGENTE
        if (isAgentInvitation) {
          console.log('[SignUp] Procesando invitación de agente - omitiendo creación de documento')
          
          // El documento de Firestore se creará en confirm-invitation
          // No crear documento aquí para evitar conflictos
          
          // Confirmar la invitación inmediatamente
          try {
            const token = await user.getIdToken()
            const confirmResponse = await fetch('/api/agents/associates/confirm-invitation', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                invitationToken: agentInvitationToken
              })
            })
            
            if (confirmResponse.ok) {
              console.log('[SignUp] ✅ Invitación de agente confirmada exitosamente')
              sessionStorage.removeItem('agentInvitationToken')
              showToast('¡Registro completado! Has sido agregado al equipo exitosamente.')
              
              // ✅ Esperar a que Vue actualice la navbar antes de redirigir
              await new Promise(resolve => setTimeout(resolve, 100))
              
              // Redirigir al dashboard de agente
              router.push('/agent-dashboard')
              return
            } else {
              const errorData = await confirmResponse.json()
              console.error('[SignUp] Error confirmando invitación:', errorData)
              throw new Error(errorData.error || 'Error confirmando invitación')
            }
          } catch (invitationError) {
            console.error('[SignUp] Error procesando invitación:', invitationError)
            throw invitationError
          }
        }

        // ── PASO 2: Construir y guardar documento de usuario ──
        const isAgent = userType.value === 'agent'

        // Generate custom verification token (solo para usuarios NO agentes)
        const verificationToken = isAgent ? null : generateSecureToken()

        // Detectar email corporativo (útil para corporate relocation y partnerships B2B)
        const FREE_EMAIL_DOMAINS = ['gmail.com','hotmail.com','yahoo.com','outlook.com','icloud.com','live.com','msn.com','proton.me','protonmail.com','yandex.com','mail.com']
        const emailDomain = email.value.split('@')[1]?.toLowerCase() || ''
        const isBusinessEmail = !!emailDomain && !FREE_EMAIL_DOMAINS.includes(emailDomain)

        // Store additional user data in Firestore
        let userData = {
          userType: userType.value,
          fullName: fullName.value,
          email: email.value,
          agreeTerms: agreeTerms.value,
          confirmAge: confirmAge.value,
          emailVerified: isAgent, // ✅ true para agentes, false para candidatos
          emailDomain,
          isBusinessEmail,
          ...(verificationToken && {
          emailVerificationToken: verificationToken,
          emailVerificationTokenCreatedAt: new Date()
          })
        };

        if (isAgent) {
          const isCommissionPlan = selectedAgentRole.value === 'commission';
          userData.agentRole = isCommissionPlan ? 'master' : selectedAgentRole.value;
          if (isCommissionPlan) {
            userData.billingType = 'commission';
          }
        }

        // Añadir campos específicos para agentes
        if (userData.userType === 'agent') {
          const isCommissionPlan = selectedAgentRole.value === 'commission';
          userData.phone = phone.value;
          userData.dni = dni.value;
          userData.agencyName = agencyName.value || '';
          userData.professionalLicense = '';
          userData.managedOwners = [];
          userData.hasCompletedProfile = true;
          userData.agentRole = isCommissionPlan ? 'master' : selectedAgentRole.value;
          userData.billingType = isCommissionPlan ? 'commission' : 'subscription';
          userData.subscriptionStatus = isCommissionPlan ? 'active' : 'pending';
        }

        await setDoc(doc(db, 'users', user.uid), userData)
        console.log('[SignUp] Documento de usuario creado en Firestore')

        // ── PASO 3: Verificar managedOwner via backend (post-auth, post-setDoc) ──
        // Solo para usuarios NO agentes (owners/applicants que podrían ser managedOwners)
        if (!isAgent && !isAgentInvitation) {
          try {
            const token = await user.getIdToken()
            const moResponse = await fetch('/api/users/check-and-link-managed-owner', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            })
            if (moResponse.ok) {
              const moResult = await moResponse.json()
              if (moResult.found) {
                console.log('[SignUp] ✅ managedOwner vinculado automáticamente:', moResult.managedOwnerId)
              }
            }
          } catch (moError) {
            console.warn('[SignUp] No se pudo verificar managedOwner (no bloqueante):', moError)
            // No afecta al flujo principal, continuamos
          }
        }

        // ============================================
        // NUEVO: Redirigir a Stripe Checkout para agentes
        // ============================================
        if (userType.value === 'agent') {
          try {
            console.log('[SignUp] Procesando registro de agente...');
            
            // ✅ PASO 1: Enviar email de bienvenida al agente
            try {
              console.log('[SignUp] Enviando email de bienvenida al agente...');
              const welcomeResponse = await fetch('/api/email/send-welcome-email', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  userEmail: email.value,
                  userName: fullName.value,
                  isAgent: true
                })
              });

              const welcomeResult = await welcomeResponse.json();
              if (welcomeResult.success) {
                console.log('[SignUp] ✅ Email de bienvenida enviado correctamente');
              } else {
                console.warn('[SignUp] ⚠️ No se pudo enviar email de bienvenida (no crítico):', welcomeResult.message);
              }
            } catch (welcomeError) {
              console.error('[SignUp] Error enviando email de bienvenida (no crítico):', welcomeError);
              // No bloquear el registro por esto
            }
            
            // ✅ PASO 2: Crear sesión de Stripe Checkout
            console.log('[SignUp] Creando sesión de Stripe Checkout para agente...');
            
            const response = await fetch('/api/subscription/create-checkout', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                userId: user.uid,
                plan: selectedAgentRole.value, // 'basic', 'master' o 'commission'
                email: email.value,
                fullName: fullName.value
              })
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error('[SignUp] Error al crear sesión de Stripe:', errorData);
              throw new Error(errorData.message || 'Error al crear sesión de pago');
            }

            const data = await response.json();
            
            console.log('[SignUp] Sesión de Stripe creada, redirigiendo...');
            
            // ✅ Mostrar mensaje de éxito antes de redirigir a pago
            showToast('¡Registro exitoso! Serás redirigido al proceso de pago...')
            
            // Pequeña espera para que se vea el mensaje
            await new Promise(resolve => setTimeout(resolve, 1500))
            
            // ✅ PASO 3: Redirigir a Stripe Checkout
            window.location.href = data.url;
            
            // No continuar con el flujo normal después de la redirección
            return;
            
          } catch (stripeError) {
            console.error('[SignUp] Error en proceso de suscripción:', stripeError);
            showToast('Error al procesar la suscripción. Por favor, contacta con soporte.');
            isLoading.value = false;
            return;
          }
        }

        // ✅ Send custom verification email (solo para candidatos, NO para agentes)
        // Los agentes ya recibieron su email de bienvenida y tienen emailVerified=true
        if (!isAgent && verificationToken) {
          try {
            console.log('[SignUp] Enviando email de verificación a candidato...');
            const recaptchaToken = await getRecaptchaTokenForApi('signup')
          const response = await fetch('/api/email/send-custom-verification', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userEmail: email.value,
              userName: fullName.value,
              verificationToken: verificationToken,
              userId: user.uid,
              isNewAccount: true,
              ...(recaptchaToken && { recaptchaToken }),
            })
          })

          const result = await response.json()
            if (result.success) {
              console.log('[SignUp] ✅ Email de verificación enviado correctamente');
            } else {
              console.error('[SignUp] Error sending verification email:', result.message)
            // No bloquear el registro por esto, solo avisar
            showToast(t('signUp.emailSentError'))
          }
        } catch (emailError) {
            console.error('[SignUp] Error sending verification email:', emailError)
          // No bloquear el registro por esto
          showToast(t('signUp.emailSentError'))
          }
        } else {
          console.log('[SignUp] Email de verificación omitido (usuario es agente)');
        }

        // LÓGICA: Vincular solicitudes temporales si existen
        if (registrationToken.value && hasPendingApplication.value) {
          try {
            const linkResponse = await fetch('/api/colivings/link-temp-applications', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                registrationToken: registrationToken.value,
                userId: user.uid,
                userEmail: user.email
              })
            })
            
            if (linkResponse.ok) {
              const linkResult = await linkResponse.json()
              
              if (linkResult.linkedApplications && linkResult.linkedApplications.length > 0) {
                showToast('¡Registro completado! Tu solicitud de coliving ha sido vinculada correctamente.')
                
                // ✅ Esperar a que Vue actualice la navbar antes de redirigir
                await new Promise(resolve => setTimeout(resolve, 100))
                
                // Redirigir a mis solicitudes en lugar de complete-profile
                router.push('/my-applications')
                return
              }
            } else {
              console.error('Error vinculando solicitudes temporales:', linkResponse.statusText)
            }
          } catch (linkError) {
            console.error('Error en vinculación de solicitudes:', linkError)
            // No bloquear el flujo normal por esto
          }
        }

        // Para applicants: mostrar panel de éxito inline con cuenta atrás
        if (userType.value === 'applicant') {
          registrationSuccess.value = true
          startRedirectCountdown()
          return
        }

        // Para otros roles (owner, etc.)
        await new Promise(resolve => setTimeout(resolve, 100))
        router.push('/complete-profile')
      } catch (error) {
        console.error('Error en el registro:', error)
        
        // Track signup error
        trackSignUpError({
          method: 'email',
          errorCode: error.code || 'unknown',
          errorMessage: error.message || '',
          userType: userType.value
        })
        
        showToast(t('signUp.error', { message: error.message }))
      } finally {
        isLoading.value = false
      }
    }

    function closeTermsModal() {
      showTermsModal.value = false
    }

    function handleTermsAccept() {
      agreeTerms.value = true
      closeTermsModal()
    }

    return {
      t,
      userType,
      selectedAgentRole,
      fullName,
      email,
      password,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      agreeTerms,
      confirmAge,
      signUp,
      signUpWithGoogle,
      handleUserTypeChange,
      showTermsModal,
      closeTermsModal,
      handleTermsAccept,
      toastMessage,
      showToast,
      showUpgradeModal,
      showPartnerModal,
      isLoading,
      // Estado registro exitoso
      registrationSuccess,
      redirectCountdown,
      goToUserDashboard,
      // Campos adicionales para agentes
      phone,
      dni,
      agencyName,
      // Variables para solicitudes temporales
      registrationToken,
      hasPendingApplication,
      pendingApplicationData,
      basicFeatures,
      proFeatures
    }
  }
}
</script>
